import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

type Props = {
  /** When true, do not notify the native host yet (e.g. settings still loading). */
  disabled?: boolean;
};

/**
 * Notifies the Qualidoc native WebView host once the shop shell can be shown.
 * Only runs inside React Native WebView (window.ReactNativeWebView).
 */
export function NativeShopShellReady({ disabled = false }: Props) {
  const router = useRouter();
  const sentRef = useRef(false);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const send = (): boolean => {
      if (sentRef.current) {
        return true;
      }
      const postMessage =
        typeof window !== 'undefined'
          ? (
              window as Window & {
                ReactNativeWebView?: { postMessage: (msg: string) => void };
              }
            ).ReactNativeWebView?.postMessage
          : undefined;
      if (!postMessage || !router.isReady) {
        return false;
      }
      sentRef.current = true;
      try {
        postMessage(JSON.stringify({ type: 'SHOP_SHELL_READY' }));
        return true;
      } catch {
        sentRef.current = false;
        return false;
      }
    };

    if (send()) {
      return;
    }

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (send() || attempts >= 30) {
        window.clearInterval(intervalId);
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [disabled, router.isReady]);

  return null;
}
